import { useState } from "react";
import { Building2, Check, Plus } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { BuildingScopeProjectOption } from "@/hooks/buildings/useBuildingScope";
import { ProjectStatusFilter } from "@/domains/projects/status/ProjectStatusFilter";
import { ProjectStatusBadge } from "@/domains/projects/status/ProjectStatusBadge";
import { useProjectStatusEnabled } from "@/domains/projects/status/projectStatusAvailability";
import {
  initialStatusTab,
  orderActiveFirst,
  partitionProjectsByStatus,
  shouldSplitByStatus,
  type ProjectStatus,
} from "@/domains/projects/status/projectStatus";

interface ProjectPickerListProps {
  projects: BuildingScopeProjectOption[];
  /** Currently scoped project, checked in the list. Omit when nothing is selected yet. */
  selectedProjectId?: number | null;
  onSelect: (projectId: number) => void;
  /**
   * Start creating a project from the picker. Omit to hide the option — the
   * caller owns the create-projects permission gate (and the dialog), so a
   * user without CREATE_PROJECTS never sees a dead button.
   */
  onCreateProject?: () => void;
  /**
   * Offer "Whole building" above the projects — leaving project scope for
   * Building view. Omit where a project must be chosen.
   */
  onSelectWholeBuilding?: () => void;
  /**
   * Whether Building view is the active scope, which is what ticks "Whole
   * building". Not inferred from a null project id: Projects view with no
   * project resolved (a rejected deep link, the scope gate) has none either.
   */
  isWholeBuildingSelected?: boolean;
}

/**
 * Searchable project list behind every project choice — the sidebar
 * view-mode toggle's dropdown (via {@link ProjectPickerPanel}), and the scope gate's chooser when it has too
 * many projects for tiles. Pure
 * presentation: the caller owns the popover and the selection side effects.
 *
 * Every row carries its status dot. Past PROJECT_STATUS_SPLIT_THRESHOLD projects
 * the list also splits into Active and Inactive tabs, opening on the tab that
 * holds the selected project (else Active); search filters within the open tab.
 */
export function ProjectPickerList({
  projects,
  selectedProjectId = null,
  onSelect,
  onCreateProject,
  onSelectWholeBuilding,
  isWholeBuildingSelected = false,
}: ProjectPickerListProps) {
  const { t } = useLanguage();
  const statusEnabled = useProjectStatusEnabled();
  // Derived until the user picks a tab, so a list that loads after the first
  // render still opens on the tab holding the selection.
  const [chosenTab, setChosenTab] = useState<ProjectStatus | null>(null);
  const tab = chosenTab ?? initialStatusTab(projects, selectedProjectId);
  const { active, inactive } = partitionProjectsByStatus(projects);
  const split = shouldSplitByStatus(projects.length, statusEnabled);
  const tabProjects = tab === "inactive" ? inactive : active;
  // Unsplit, it's one list: active projects above inactive ones.
  const shown = split ? tabProjects : orderActiveFirst(projects);
  // The check column exists whenever there is something to mark — a selected
  // project, or "Whole building" standing for no project. With neither (the
  // scope gate) it would be an empty indent.
  const showChecks = selectedProjectId != null || onSelectWholeBuilding != null;

  return (
    <Command>
      {/* Search first: the popover focuses its first tabbable element, and
          typing straight into the search is what opening the picker is for. */}
      <CommandInput placeholder={t.buildings.scope.searchPlaceholder} />
      {split && (
        <ProjectStatusFilter
          value={tab}
          onValueChange={(value) => {
            if (value !== "all") setChosenTab(value);
          }}
          counts={{ active: active.length, inactive: inactive.length, all: projects.length }}
          includeAll={false}
          className="px-1"
        />
      )}
      <CommandList>
        {onSelectWholeBuilding && (
          // First in the list (cmdk only navigates items inside it) and
          // forceMounted, so neither the status tab nor a search ever hides
          // the way back to the whole building.
          <CommandGroup forceMount className="border-b">
            <CommandItem forceMount value="__whole_building__" onSelect={onSelectWholeBuilding}>
              <Check
                className={cn(
                  "mr-2 h-4 w-4",
                  isWholeBuildingSelected ? "opacity-100" : "opacity-0"
                )}
              />
              <Building2 className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate">{t.buildings.sidebar.wholeBuilding}</span>
            </CommandItem>
          </CommandGroup>
        )}
        {/* An empty tab says so whatever else the list holds — CommandEmpty
            below only covers a search that filters a non-empty tab to nothing. */}
        {split && shown.length === 0 && (
          <p className="px-2 py-4 text-center text-sm text-muted-foreground">
            {tab === "inactive"
              ? t.projects.status.noInactiveProjects
              : t.projects.status.noActiveProjects}
          </p>
        )}
        {/* cmdk never registers forceMount items, so the "New project" row
            below doesn't count toward filtered.count and CommandEmpty would
            render alongside it. With a create option present, the row itself
            is the empty state — creating is exactly what you want when the
            name you searched for doesn't exist. */}
        {/* The same holds for the forceMounted "Whole building" row: beside
            it, "No projects found" would contradict the row still on show. */}
        {!onCreateProject && !onSelectWholeBuilding && (!split || shown.length > 0) && (
          <CommandEmpty>{t.buildings.scope.noProjects}</CommandEmpty>
        )}
        <CommandGroup>
          {shown.map((project) => (
            <CommandItem
              key={project.id}
              value={project.name}
              onSelect={() => onSelect(project.id)}
            >
              {showChecks && (
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    selectedProjectId === project.id ? "opacity-100" : "opacity-0"
                  )}
                />
              )}
              <ProjectStatusBadge variant="dot" status={project.status} className="mr-2" />
              <span className="truncate">{project.name}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        {onCreateProject && (
          <>
            {/* `alwaysRender` / `forceMount`: cmdk filters the list as you
                type — this stays the last option no matter the query. */}
            <CommandSeparator alwaysRender />
            <CommandGroup forceMount>
              {/* Namespaced value: project rows use project.name, so a plain
                  "new-project" could collide with a real project's name in
                  cmdk's value-based identity (both would render selected and
                  Enter could pick the wrong row). */}
              <CommandItem forceMount value="__create_new_project__" onSelect={onCreateProject}>
                <Plus className="mr-2 h-4 w-4 shrink-0" />
                <span className="truncate">{t.projects.selector.newProject}</span>
              </CommandItem>
            </CommandGroup>
          </>
        )}
      </CommandList>
    </Command>
  );
}
